import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { DatabaseIcon, BarChart2Icon, UsersIcon, AlertTriangleIcon, NetworkIcon, BrainIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
const contextItems = [{
  icon: BarChart2Icon,
  label: 'Historical Delivery Patterns',
  description: 'Sprint velocity, release cadence, and throughput baselines',
  color: '#00D4FF'
}, {
  icon: UsersIcon,
  label: 'Team Benchmarks',
  description: 'Performance norms by team, role, and seniority level',
  color: '#3B82F6'
}, {
  icon: DatabaseIcon,
  label: 'Performance Baselines',
  description: 'DORA metrics, cycle time, and quality benchmarks',
  color: '#A855F7'
}, {
  icon: AlertTriangleIcon,
  label: 'Deployment Risk Signals',
  description: 'Historical incident patterns and high-risk change indicators',
  color: '#F59E0B'
}, {
  icon: NetworkIcon,
  label: 'Architectural Intelligence',
  description: 'Codebase topology, dependency graphs, and hotspot analysis',
  color: '#10B981'
}];
const benefits = ['Agents no longer operate blindly', 'Context-aware code generation', 'Risk-informed deployment decisions', 'Team-calibrated suggestions', 'Safer, smarter automation at scale'];
export function MCPSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-24 px-6 bg-[#080C14] relative overflow-hidden" data-id="element-233">
      {/* Background glow */}
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] opacity-10" style={{
      background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)'
    }} data-id="element-234" />
      <div className="absolute top-0 left-0 w-[400px] h-[400px] opacity-5" style={{
      background: 'radial-gradient(circle, #00D4FF 0%, transparent 70%)'
    }} data-id="element-235" />

      <div className="max-w-6xl mx-auto relative" data-id="element-236">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-4" data-id="element-237">
          <span className="text-xs font-bold tracking-[0.25em] text-cyan-400 uppercase" data-id="element-238">
            MCP for External Agents
          </span>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.1,
        duration: 0.6
      }} className="mb-12" data-id="element-239">
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-3" data-id="element-240">
            Equip AI Agents with
            <br data-id="element-241" />
            <span className="text-transparent bg-clip-text" style={{
            backgroundImage: 'linear-gradient(135deg, #00D4FF 0%, #3B82F6 100%)'
          }} data-id="element-242">
              Organizational Context
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl" data-id="element-243">
            Waydev introduces MCP integration to give external agents the
            intelligence they need to operate safely and effectively within your
            engineering organization.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-id="element-244">
          {/* Context items */}
          <div className="space-y-3" data-id="element-245">
            {contextItems.map((item, i) => {
            const Icon = item.icon;
            return <motion.div key={item.label} initial={{
              opacity: 0,
              x: -30
            }} animate={isInView ? {
              opacity: 1,
              x: 0
            } : {}} transition={{
              delay: 0.2 + i * 0.1,
              duration: 0.5
            }} className="flex items-start gap-4 p-4 rounded-xl border border-[#1E2D45] bg-[#0D1421]" data-id="element-246">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${item.color}15`
              }} data-id="element-247">
                    <Icon className="w-4 h-4" style={{
                  color: item.color
                }} data-id="element-248" />
                  </div>
                  <div data-id="element-249">
                    <div className="text-white font-semibold text-sm mb-0.5" data-id="element-250">
                      {item.label}
                    </div>
                    <div className="text-gray-500 text-xs leading-relaxed" data-id="element-251">
                      {item.description}
                    </div>
                  </div>
                  <ArrowRightIcon className="w-4 h-4 text-gray-700 flex-shrink-0 mt-2" data-id="element-252" />
                </motion.div>;
          })}
          </div>

          {/* Right panel */}
          <div className="space-y-4" data-id="element-253">
            {/* MCP diagram */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            delay: 0.3,
            duration: 0.6
          }} className="bg-[#0D1421] border border-[#1E2D45] rounded-2xl p-6" data-id="element-254">
              <div className="flex items-center gap-3 mb-6" data-id="element-255">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-400/10" data-id="element-256">
                  <BrainIcon className="w-5 h-5 text-cyan-400" data-id="element-257" />
                </div>
                <div data-id="element-258">
                  <div className="text-white font-bold" data-id="element-259">Waydev MCP Server</div>
                  <div className="text-gray-500 text-xs" data-id="element-260">
                    Model Context Protocol Integration
                  </div>
                </div>
              </div>

              {/* Flow diagram */}
              <div className="space-y-2" data-id="element-261">
                {[{
                label: 'External Agent (Claude, Cursor, Devin)',
                color: '#F97316',
                side: 'agent'
              }, {
                label: '↕ MCP Protocol',
                color: '#6B7280',
                side: 'protocol'
              }, {
                label: 'Waydev Intelligence Layer',
                color: '#00D4FF',
                side: 'waydev'
              }, {
                label: '↕ Enriched Context',
                color: '#6B7280',
                side: 'protocol'
              }, {
                label: 'Your Engineering Data',
                color: '#3B82F6',
                side: 'data'
              }].map((node, i) => <motion.div key={i} initial={{
                opacity: 0,
                scale: 0.95
              }} animate={isInView ? {
                opacity: 1,
                scale: 1
              } : {}} transition={{
                delay: 0.5 + i * 0.08
              }} className={`${node.side === 'protocol' ? 'text-center py-1' : 'px-4 py-2.5 rounded-lg border'}`} style={node.side !== 'protocol' ? {
                backgroundColor: `${node.color}10`,
                borderColor: `${node.color}30`
              } : {}} data-id="element-262">
                    <span className={`text-sm font-medium ${node.side === 'protocol' ? 'text-gray-600' : ''}`} style={node.side !== 'protocol' ? {
                  color: node.color
                } : {}} data-id="element-263">
                      {node.label}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Benefits */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            delay: 0.7,
            duration: 0.6
          }} className="bg-[#0D1421] border border-[#1E2D45] rounded-2xl p-6" data-id="element-264">
              <div className="text-white font-semibold mb-4" data-id="element-265">
                What This Unlocks
              </div>
              <div className="space-y-2.5" data-id="element-266">
                {benefits.map((benefit, i) => <motion.div key={benefit} initial={{
                opacity: 0,
                x: 10
              }} animate={isInView ? {
                opacity: 1,
                x: 0
              } : {}} transition={{
                delay: 0.8 + i * 0.07
              }} className="flex items-center gap-3" data-id="element-267">
                    <div className="w-5 h-5 rounded-full bg-cyan-400/15 flex items-center justify-center flex-shrink-0" data-id="element-268">
                      <CheckIcon className="w-3 h-3 text-cyan-400" data-id="element-269" />
                    </div>
                    <span className="text-gray-300 text-sm" data-id="element-270">{benefit}</span>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Final CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.9,
        duration: 0.6
      }} className="mt-16 text-center" data-id="element-271">
          <div className="inline-block px-8 py-6 rounded-2xl border mb-8" style={{
          background: 'linear-gradient(135deg, rgba(0,212,255,0.06) 0%, rgba(59,130,246,0.06) 100%)',
          borderColor: 'rgba(0,212,255,0.2)'
        }} data-id="element-272">
            <div className="text-2xl md:text-3xl font-black text-white mb-2" data-id="element-273">
              Agents no longer operate blindly.
            </div>
            <div className="text-xl font-bold text-cyan-400" data-id="element-274">
              They operate with context.
            </div>
          </div>

          <div data-id="element-275">
            <a href="https://waydev.co/demo" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-base font-bold text-[#080C14] transition-all duration-200 hover:scale-105 active:scale-95" style={{
            background: 'linear-gradient(135deg, #00D4FF 0%, #3B82F6 100%)',
            boxShadow: '0 0 32px rgba(0, 212, 255, 0.4)'
          }} data-id="element-276">
              Get a Demo
              <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-277">
                <path d="M1 7h12M7 1l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-278" />
              </svg>
            </a>
            <p className="text-gray-600 text-xs mt-3" data-id="element-279">waydev.co/demo</p>
          </div>
        </motion.div>
      </div>
    </section>;
}